/**
 * DS-Wiki Masters Management Page
 * Provides tabbed interface for managing all DS-Wiki master entities
 */

import { useState, useEffect, useMemo } from 'react';
import { dsWikiService } from '../services';
import type {
  Department,
  DepartmentInput,
  ArticleCategory,
  ArticleCategoryInput,
  Tag,
  TagInput,
  Workspace,
  WorkspaceInput,
  ArticleType,
  ArticleTypeInput,
  ArticleStatus,
  ArticleStatusInput,
  SectionType,
  SectionTypeInput,
  SuggestionType,
  SuggestionTypeInput,
  Curator,
  CuratorInput,
} from '../services/types';
import { TableSkeleton } from '@/components/base/Skeleton';

// Master entity types
type MasterType = 'department' | 'article-category' | 'tag' | 'workspace' | 'article-type' | 'article-status' | 'section-type' | 'suggestion-type' | 'curator';

interface MasterTab {
  id: MasterType;
  label: string;
  icon: string;
  description: string;
}

const masterTabs: MasterTab[] = [
  { id: 'department', label: 'Departments', icon: 'ri-building-line', description: 'Manage organizational departments' },
  { id: 'article-category', label: 'Article Categories', icon: 'ri-folder-line', description: 'Manage article categories' },
  { id: 'tag', label: 'Tags', icon: 'ri-price-tag-3-line', description: 'Manage content tags' },
  { id: 'workspace', label: 'Workspaces', icon: 'ri-layout-grid-line', description: 'Manage workspaces' },
  { id: 'article-type', label: 'Article Types', icon: 'ri-file-text-line', description: 'Manage article types' },
  { id: 'article-status', label: 'Article Statuses', icon: 'ri-checkbox-circle-line', description: 'Manage article statuses' },
  { id: 'section-type', label: 'Section Types', icon: 'ri-layout-row-line', description: 'Manage section types' },
  { id: 'suggestion-type', label: 'Suggestion Types', icon: 'ri-lightbulb-line', description: 'Manage suggestion types' },
  { id: 'curator', label: 'Curators', icon: 'ri-user-star-line', description: 'Manage curators' },
];

// Generic form modal component
interface FormModalProps<T> {
  title: string;
  editingItem: T | null;
  fields: FormField[];
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}

interface FormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'color';
  required?: boolean;
  placeholder?: string;
}

function FormModal<T extends Record<string, any>>({ title, editingItem, fields, onSave, onCancel }: FormModalProps<T>) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editingItem) {
      setFormData(editingItem);
    } else {
      const initial: Record<string, any> = {};
      fields.forEach(f => {
        initial[f.name] = f.type === 'boolean' ? true : f.type === 'number' ? 0 : '';
      });
      setFormData(initial);
    }
  }, [editingItem, fields]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave(formData);
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {fields.map(field => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.type === 'boolean' ? (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!formData[field.name]}
                    onChange={e => setFormData({ ...formData, [field.name]: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Active</span>
                </label>
              ) : field.type === 'color' ? (
                <input
                  type="color"
                  value={formData[field.name] || '#3B82F6'}
                  onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                  className="w-20 h-10 p-1 rounded border border-gray-300"
                />
              ) : (
                <input
                  type={field.type}
                  value={formData[field.name] || ''}
                  onChange={e => setFormData({ ...formData, [field.name]: field.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DSWikiMasters() {
  const [activeTab, setActiveTab] = useState<MasterType>('department');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  // Data states for each master type
  const [departments, setDepartments] = useState<Department[]>([]);
  const [articleCategories, setArticleCategories] = useState<ArticleCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [articleTypes, setArticleTypes] = useState<ArticleType[]>([]);
  const [articleStatuses, setArticleStatuses] = useState<ArticleStatus[]>([]);
  const [sectionTypes, setSectionTypes] = useState<SectionType[]>([]);
  const [suggestionTypes, setSuggestionTypes] = useState<SuggestionType[]>([]);
  const [curators, setCurators] = useState<Curator[]>([]);

  // Fetch data based on active tab
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'department':
          const deptRes = await dsWikiService.getAllDepartments();
          setDepartments(deptRes.data || []);
          break;
        case 'article-category':
          const catRes = await dsWikiService.getAllArticleCategories();
          setArticleCategories(catRes.data || []);
          break;
        case 'tag':
          const tagRes = await dsWikiService.getAllTags();
          setTags(tagRes.data || []);
          break;
        case 'workspace':
          const wsRes = await dsWikiService.getAllWorkspaces();
          setWorkspaces(wsRes.data || []);
          break;
        case 'article-type':
          const atRes = await dsWikiService.getAllArticleTypes();
          setArticleTypes(atRes.data || []);
          break;
        case 'article-status':
          const asRes = await dsWikiService.getAllArticleStatuses();
          setArticleStatuses(asRes.data || []);
          break;
        case 'section-type':
          const stRes = await dsWikiService.getAllSectionTypes();
          setSectionTypes(stRes.data || []);
          break;
        case 'suggestion-type':
          const sgRes = await dsWikiService.getAllSuggestionTypes();
          setSuggestionTypes(sgRes.data || []);
          break;
        case 'curator':
          const crRes = await dsWikiService.getAllCurators();
          setCurators(crRes.data || []);
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get current data based on active tab
  const getCurrentData = (): any[] => {
    switch (activeTab) {
      case 'department': return departments;
      case 'article-category': return articleCategories;
      case 'tag': return tags;
      case 'workspace': return workspaces;
      case 'article-type': return articleTypes;
      case 'article-status': return articleStatuses;
      case 'section-type': return sectionTypes;
      case 'suggestion-type': return suggestionTypes;
      case 'curator': return curators;
      default: return [];
    }
  };

  // Get form fields based on active tab
  const getFormFields = (): FormField[] => {
    switch (activeTab) {
      case 'department':
        return [
          { name: 'dpt_nm', label: 'Department Name', type: 'text', required: true },
          { name: 'dpt_cd', label: 'Department Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'icon_tx', label: 'Icon', type: 'text', placeholder: 'e.g., ri-building-line' },
          { name: 'clr_cd', label: 'Color', type: 'color' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'article-category':
        return [
          { name: 'artcl_ctgry_nm', label: 'Category Name', type: 'text', required: true },
          { name: 'artcl_ctgry_cd', label: 'Category Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'icon_tx', label: 'Icon', type: 'text' },
          { name: 'clr_cd', label: 'Color', type: 'color' },
          { name: 'rfrnc_prfx_tx', label: 'Reference Prefix', type: 'text' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'tag':
        return [
          { name: 'tag_nm', label: 'Tag Name', type: 'text', required: true },
          { name: 'tag_cd', label: 'Tag Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'clr_cd', label: 'Color', type: 'color' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'workspace':
        return [
          { name: 'wrksp_nm', label: 'Workspace Name', type: 'text', required: true },
          { name: 'wrksp_cd', label: 'Workspace Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'icon_tx', label: 'Icon', type: 'text' },
          { name: 'clr_cd', label: 'Color', type: 'color' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'is_dflt_in', label: 'Default', type: 'boolean' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'article-type':
        return [
          { name: 'artcl_type_nm', label: 'Type Name', type: 'text', required: true },
          { name: 'artcl_type_cd', label: 'Type Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'article-status':
        return [
          { name: 'artcl_sts_nm', label: 'Status Name', type: 'text', required: true },
          { name: 'artcl_sts_cd', label: 'Status Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'clr_cd', label: 'Color', type: 'color' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'section-type':
        return [
          { name: 'sctn_type_nm', label: 'Section Type Name', type: 'text', required: true },
          { name: 'sctn_type_cd', label: 'Section Type Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'icon_tx', label: 'Icon', type: 'text' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'suggestion-type':
        return [
          { name: 'sgtn_type_nm', label: 'Suggestion Type Name', type: 'text', required: true },
          { name: 'sgtn_type_cd', label: 'Suggestion Type Code', type: 'text' },
          { name: 'dscn_tx', label: 'Description', type: 'text' },
          { name: 'clr_cd', label: 'Color', type: 'color' },
          { name: 'sqnce_nu', label: 'Sequence', type: 'number' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      case 'curator':
        return [
          { name: 'usr_id', label: 'User ID', type: 'number', required: true },
          { name: 'crtr_nm', label: 'Curator Name', type: 'text' },
          { name: 'dpt_id', label: 'Department ID', type: 'number' },
          { name: 'is_snr_in', label: 'Senior Curator', type: 'boolean' },
          { name: 'a_in', label: 'Status', type: 'boolean' },
        ];
      default:
        return [];
    }
  };

  // Get ID field name based on active tab
  const getIdField = (): string => {
    switch (activeTab) {
      case 'department': return 'dswiki_dpt_id';
      case 'article-category': return 'dswiki_artcl_ctgry_id';
      case 'tag': return 'dswiki_tag_id';
      case 'workspace': return 'dswiki_wrksp_id';
      case 'article-type': return 'dswiki_artcl_type_id';
      case 'article-status': return 'dswiki_artcl_sts_id';
      case 'section-type': return 'dswiki_sctn_type_id';
      case 'suggestion-type': return 'dswiki_sgtn_type_id';
      case 'curator': return 'dswiki_crtr_id';
      default: return 'id';
    }
  };

  // Get name field based on active tab
  const getNameField = (): string => {
    switch (activeTab) {
      case 'department': return 'dpt_nm';
      case 'article-category': return 'artcl_ctgry_nm';
      case 'tag': return 'tag_nm';
      case 'workspace': return 'wrksp_nm';
      case 'article-type': return 'artcl_type_nm';
      case 'article-status': return 'artcl_sts_nm';
      case 'section-type': return 'sctn_type_nm';
      case 'suggestion-type': return 'sgtn_type_nm';
      case 'curator': return 'crtr_nm';
      default: return 'name';
    }
  };

  // Get current tab reference
  const currentTab = masterTabs.find(t => t.id === activeTab)!;

  // Save handler
  const handleSave = async (data: any) => {
    const isEditing = !!editingItem;
    const entityName = currentTab.label.replace(/s$/, '');

    console.log('handleSave called with data:', data);
    console.log('activeTab:', activeTab);
    console.log('isEditing:', isEditing);

    try {
      let result;
      switch (activeTab) {
        case 'department':
          console.log('Calling saveDepartment...');
          result = await dsWikiService.saveDepartment(data);
          console.log('saveDepartment result:', result);
          break;
        case 'article-category':
          result = await dsWikiService.saveArticleCategory(data);
          break;
        case 'tag':
          result = await dsWikiService.saveTag(data);
          break;
        case 'workspace':
          result = await dsWikiService.saveWorkspace(data);
          break;
        case 'article-type':
          result = await dsWikiService.saveArticleType(data);
          break;
        case 'article-status':
          result = await dsWikiService.saveArticleStatus(data);
          break;
        case 'section-type':
          result = await dsWikiService.saveSectionType(data);
          break;
        case 'suggestion-type':
          result = await dsWikiService.saveSuggestionType(data);
          break;
        case 'curator':
          result = await dsWikiService.saveCurator(data);
          break;
      }

      console.log('Save result:', result);

      // Show success message
      setSuccessMessage(`${entityName} ${isEditing ? 'updated' : 'created'} successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);

      setShowFormModal(false);
      setEditingItem(null);
      await fetchData();
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.message || `Failed to save ${entityName}`);
      throw err; // Re-throw to let FormModal handle it too
    }
  };

  // Delete handler
  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      switch (activeTab) {
        case 'department':
          await dsWikiService.deleteDepartment(id);
          break;
        case 'article-category':
          await dsWikiService.deleteArticleCategory(id);
          break;
        case 'tag':
          await dsWikiService.deleteTag(id);
          break;
        case 'workspace':
          await dsWikiService.deleteWorkspace(id);
          break;
        case 'article-type':
          await dsWikiService.deleteArticleType(id);
          break;
        case 'article-status':
          await dsWikiService.deleteArticleStatus(id);
          break;
        case 'section-type':
          await dsWikiService.deleteSectionType(id);
          break;
        case 'suggestion-type':
          await dsWikiService.deleteSuggestionType(id);
          break;
        case 'curator':
          await dsWikiService.deleteCurator(id);
          break;
      }
      await fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete');
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    const data = getCurrentData();
    if (!searchQuery.trim()) return data;

    const nameField = getNameField();
    const query = searchQuery.toLowerCase();
    return data.filter((item: any) => {
      const name = item[nameField] || '';
      return name.toLowerCase().includes(query);
    });
  }, [activeTab, departments, articleCategories, tags, workspaces, articleTypes, articleStatuses, sectionTypes, suggestionTypes, curators, searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 module-header shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">DS-Wiki Masters</h1>
              <p className="text-blue-100 text-sm">Manage master data for DS-Wiki module</p>
            </div>
            <button
              onClick={() => { setEditingItem(null); setShowFormModal(true); }}
              className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 flex items-center font-medium shadow-sm"
            >
              <i className="ri-add-line mr-2"></i>
              Add {currentTab.label.replace(/s$/, '')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pb-0 overflow-x-auto">
          <div className="flex gap-1">
            {masterTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
                className={`px-4 py-2 rounded-t-lg font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Description and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center`}>
                <i className={`${currentTab.icon} text-2xl text-blue-600`}></i>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{currentTab.label}</h2>
                <p className="text-sm text-gray-500">{currentTab.description}</p>
              </div>
            </div>
            <div className="relative w-full md:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={`Search ${currentTab.label.toLowerCase()}...`}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
            <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
            <div>
              <h3 className="font-semibold text-green-800">Success</h3>
              <p className="text-sm text-green-600">{successMessage}</p>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-auto text-green-600 hover:text-green-800"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <i className="ri-error-warning-line text-red-600 text-xl"></i>
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <TableSkeleton />
        ) : filteredData.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <i className={`${currentTab.icon} text-6xl text-gray-300 mb-4`}></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No {currentTab.label.toLowerCase()} found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery ? 'Try adjusting your search query' : `Get started by adding your first ${currentTab.label.toLowerCase().replace(/s$/, '')}`}
            </p>
            {!searchQuery && (
              <button
                onClick={() => { setEditingItem(null); setShowFormModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <i className="ri-add-line mr-2"></i>
                Add {currentTab.label.replace(/s$/, '')}
              </button>
            )}
          </div>
        ) : (
          /* Data Table */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.map((item: any, index: number) => {
                    const idField = getIdField();
                    const nameField = getNameField();
                    const codeField = nameField.replace('_nm', '_cd');

                    return (
                      <tr key={item[idField]} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {item.clr_cd && (
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: item.clr_cd }}
                              ></span>
                            )}
                            {item.icon_tx && (
                              <i className={`${item.icon_tx} text-gray-500`}></i>
                            )}
                            <span className="font-medium text-gray-900">{item[nameField] || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{item[codeField] || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{item.dscn_tx || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            item.a_in
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {item.a_in ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => { setEditingItem(item); setShowFormModal(true); }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit"
                            >
                              <i className="ri-edit-line text-lg"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(item[idField], item[nameField])}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-lg"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
              Showing {filteredData.length} of {getCurrentData().length} {currentTab.label.toLowerCase()}
            </div>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <FormModal
          title={editingItem ? `Edit ${currentTab.label.replace(/s$/, '')}` : `Add ${currentTab.label.replace(/s$/, '')}`}
          editingItem={editingItem}
          fields={getFormFields()}
          onSave={handleSave}
          onCancel={() => { setShowFormModal(false); setEditingItem(null); }}
        />
      )}
    </div>
  );
}
